import Head from "next/head";
import Script from "next/script";
import { useTeamConfig } from "../hooks/useTeamConfig";
import { globalConfig } from "../config/global_config";
import { v4 as uuid } from "uuid";
import React from "react";

export function HeaderScripts() {
  const { analyticsConfigs: globalAnalyticsConfigs } = globalConfig;
  const teamConfig = useTeamConfig();
  const { analyticsConfigs, isCrowdHandlerEnabled } = teamConfig;
  const {
    AnalyticsHeader,
    beaconId,
    metaPixelId,
    attrkPixelId,
    amazonPixelId,
    googleAnalyticsId,
    stackAdaptPixelId,
    googleTagManagerId,
    amazonTrackingEvent,
  } = analyticsConfigs;

  return (
    <>
      {/* Team specific header for analytics, can attach multiple script in isolation */}
      {AnalyticsHeader ? <AnalyticsHeader teamConfig={teamConfig} /> : null}

      {/* Seasonshare Google Analytics */}
      {globalAnalyticsConfigs.googleAnalyticsId ? (
        <>
          <Script
            strategy="afterInteractive"
            id="ss-google-analytics-script-1"
            src={`https://www.googletagmanager.com/gtag/js?id=${globalAnalyticsConfigs.googleAnalyticsId}`}
          />
          <Script
            nonce={process.env.ssGoogleAnalyticsScriptNonce}
            strategy="afterInteractive"
            id="ss-google-analytics-script-2"
            dangerouslySetInnerHTML={{
              __html: `
          window.dataLayer = window.dataLayer || []; 
          function gtag(){dataLayer.push(arguments);} 
          gtag('js', new Date()); gtag('config', '${globalAnalyticsConfigs.googleAnalyticsId}');`,
            }}
          />
        </>
      ) : null}

      {/* Crowd Handler script */}
      {/* ========================================= */}
      {process.env.NODE_ENV == "production" ? (
        <Head>
          <script
            nonce={process.env.crowdHandlerScriptNonce}
            src={`https://wait.crowdhandler.com/js/latest/main.js?id=5005898fce513ecad75524d94ddea75d844aef01f7604f4162fc367ac32dc4dc`}
            id="crowd-handler-script"
          />
        </Head>
      ) : null}

      {/* Attrk Pixel script */}
      {attrkPixelId ? (
        <img
          id="attrk-pixel"
          src={`https://arttrk.com/pixel/?ad_log=referer&action=content&pixid=${attrkPixelId}`}
          width="1"
          height="1"
          style={{ border: "0", display: "none" }}
          alt="attrk pixel"
        />
      ) : null}

      {/* Google Analytics script */}
      {/* ========================================= */}
      {googleAnalyticsId ? (
        <Script
          src={`https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`}
          id="google-analytics-script-1"
          strategy="afterInteractive"
        />
      ) : null}

      {googleAnalyticsId ? (
        <Script
          nonce={process.env.teamGoogleAnalyticsScriptNonce}
          id="google-analytics-script-2"
          strategy="afterInteractive"
          dangerouslySetInnerHTML={{
            __html: `
              window.dataLayer = window.dataLayer || [];
              function gtag() {
                dataLayer.push(arguments);
              }
              gtag("js", new Date());
              gtag("config", "${googleAnalyticsId}");
            `,
          }}
        />
      ) : null}

      {googleTagManagerId ? (
        <Head>
          <script
            id="google-tag-manager-script"
            nonce={process.env.googleTagManagerNonce}
          >{`
            // polyfill to support window.crypto.randomUUID() which is a new feature not supported by all browser. 
            window.crypto.randomUUID = function () {
              return '${uuid()}';
            };
            // tag manager script integration
            (function (w, d, s, l, i) {
              w[l] = w[l] || [];
              w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
              var f = d.getElementsByTagName(s)[0],
                j = d.createElement(s),
                dl = l != "dataLayer" ? "&l=" + l : "";
              j.setAttribute('nonce', '${
                process.env.googleTagManagerInlineScriptNonce
              }');
              j.async = true;
              j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
              f.parentNode.insertBefore(j, f);
            })(window, document, "script", "dataLayer", "${googleTagManagerId}");
            console.log("====Google Tag Manager loaded====");
        `}</script>
        </Head>
      ) : null}

      {googleTagManagerId ? (
        <noscript id="tag-manager-noscript">
          <iframe
            height="0"
            width="0"
            style={{ display: "none", visibility: "hidden" }}
            src={`https://www.googletagmanager.com/ns.html?id=${googleTagManagerId}`}
          ></iframe>
        </noscript>
      ) : null}

      {/* Beacon analytics script */}
      {/* ========================================= */}
      {beaconId ? (
        <Script
          id="beacon-analytics-script"
          type="text/javascript"
          strategy="afterInteractive"
          src="https://cdn.evgnet.com/beacon/pistonssports/prod/scripts/evergage.min.js"
        />
      ) : null}

      {/* Meta Pixel script */}
      {/* ========================================= */}
      {metaPixelId ? (
        <Head key={uuid()}>
          <script nonce={process.env.pixelScriptNonce} id="meta-pixel-script">
            {`
            !(function (f, b, e, v, n, t, s) {
            if (f.fbq) return;
            n = f.fbq = function () {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
            };
            if (!f._fbq) f._fbq = n;
            n.push = n;
            n.loaded = !0;
            n.version = "2.0";
            n.queue = [];
            t = b.createElement(e);
            t.async = !0;
            t.src = v;
            s = b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t, s);
            })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");
            fbq("init", ${metaPixelId});
            fbq('track', 'PageView')
            console.log("====Meta Pixel loaded===");
            `}
          </script>
        </Head>
      ) : null}

      {amazonPixelId && amazonTrackingEvent ? (
        <script>
          {`!function(w, d, s, t, a) {
            if (w.amzn) return;
              w.amzn = a = function() {
                w.amzn.q.push([arguments, (new Date).getTime()])
              };
              a.q = [];
              a.version = "0.0";
              s = d.createElement("script");
              s.src = https://c.amazon-adsystem.com/aat/amzn.js;
              s.id = "amzn-pixel";
              s.async = true;
              t = d.getElementsByTagName("script")[0];
              t.parentNode.insertBefore(s, t)
            }
            (window, document);
            amzn("setRegion", "NA"); 
            amzn("addTag", "${amazonPixelId}"); 
            amzn("trackEvent", "${amazonTrackingEvent}");
          `}
        </script>
      ) : null}

      {stackAdaptPixelId ? (
        <script>
          {`!function(s, a, e, v, n, t, z) {
              if (s.saq) return;
              n = s.saq = function() {
                n.callMethod?n.callMethod.apply(n,arguments): n.queue.push(arguments)
              };
              if (!s._saq) s._saq=n;
              n.push=n;
              n.loaded=!0;
              n.version='1.0';
              n.queue=[];
              t=a.createElement(e);
              t.async=!0;
              t.src=v;
              z=a.getElementsByTagName(e)[0];
              z.parentNode.insertBefore(t,z)
            }
            (window,document,'script','https://tags.srv.stackadapt.com/events.js');
            saq('ts','${stackAdaptPixelId}');`}
        </script>
      ) : null}
    </>
  );
}
